import { useMemo, useState, useEffect, useRef } from 'react';
import { Box, Divider, IconButton, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import Iconify from 'src/components/iconify';
import { useVisibilityContext } from './VisibilityProvider';
import { useLocales } from 'src/locales';

type Term = {
  label: string;
  score: number; // 0-100
};

interface TopAttackedSearchTermsProps {
  terms?: Term[];
  loading?: boolean;
  barColor?: string;
  onSelectTerm?: (term: string) => void;
  onResetFilter?: () => void;
  isClientDomain?: boolean;
}

// Lightweight component showing top attacked search terms with compact/expanded views
export default function TopAttackedSearchTerms(props: TopAttackedSearchTermsProps) {
  
  const { t } = useLocales();
  const { terms, loading = false, barColor = '#C09910', onSelectTerm, onResetFilter, isClientDomain = false } = props;

  const { selectedCountry, filterStore } = useVisibilityContext();
  const countriesPayload: string[] = useMemo(() => {
    if (selectedCountry) return [selectedCountry];
    if (Array.isArray(filterStore?.countries) && filterStore.countries.length > 0) return filterStore.countries;
    return [];
  }, [selectedCountry, filterStore?.countries]);

  // Placeholder data while API integration is wired
  const placeholderTerms: Term[] = useMemo(
    () => [
      { label: 'sac polene', score: 96 },
      { label: 'clutch wallet', score: 92 },
      { label: 'crossbody purse', score: 78 },
      { label: 'duffle bag', score: 89 },
      { label: 'tote bag', score: 85 },
    ],
    []
  );

  // Map API response to Term[] with normalized score (0-100 based on max searches)
  const fetchedTerms: Term[] = useMemo(() => {
    const keywords = filterStore?.loadedKeywords as Array<{
      search_term: string;
      total_organic_searches: number;
    }> | undefined;
    if (!keywords || keywords.length === 0) return [];
    const displayed_keywords = 200;
    const sliced_keywords = [...keywords]
      .sort((a, b) => (b.total_organic_searches ?? 0) - (a.total_organic_searches ?? 0))
      .slice(0, displayed_keywords);
    const maxValue = Math.max(
      0,
      ...sliced_keywords.map((k) => (typeof k.total_organic_searches === 'number' ? k.total_organic_searches : 0))
    );
    if (maxValue <= 0) return sliced_keywords.map((k) => ({ label: k.search_term, score: 0 }));
    return sliced_keywords.map((k) => ({
      label: k.search_term,
      score: Math.round(((typeof k.total_organic_searches === 'number' ? k.total_organic_searches : 0) * 100) / maxValue),
    }));
  }, [filterStore?.loadedKeywords]);

  const isLoading = loading;
  const data = terms && terms.length > 0 ? terms : (fetchedTerms.length > 0 ? fetchedTerms : placeholderTerms);
  const filteredData = useMemo(() => (data || []).filter((t) => (t?.score ?? 0) >= 1), [data]);
  const [expanded, setExpanded] = useState<boolean>(false);

  // Get selected terms from Ads-specific PLA keywords
  const selectedTerms = useMemo(() => {
    const keywords = filterStore?.keywordsPLA as string[] | undefined;
    return keywords && keywords.length > 0 ? keywords : [];
  }, [filterStore?.keywordsPLA]);

  return (
    <Paper variant="outlined" sx={{ p: 2, position: 'relative' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
      <Tooltip placement="top" arrow title={t(isClientDomain ? 'definitions.top_search_terms' : 'definitions.top_attacked_search_terms')}>
        <Typography
          variant="subtitle2"
          sx={{
            fontSize: "13px",
            fontWeight: "700",
          }}
        >{t(isClientDomain ? 'dashboard.global.visibility.top_search_terms' : 'dashboard.global.visibility.top_attacked_search_terms')}</Typography>
        </Tooltip>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {selectedTerms.length > 0 && onResetFilter && (
            <IconButton size="small" onClick={onResetFilter} aria-label="Reset filter">
              <Iconify icon="eva:close-circle-outline" width={18} />
            </IconButton>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            {t('dashboard.global.visibility.sorted_by_brand_search')}
          </Typography>
          <IconButton size="small" onClick={() => setExpanded((e) => !e)} aria-label={expanded ? 'Collapse' : 'Expand'}>
            <Iconify icon={expanded ? 'eva:collapse-outline' : 'eva:expand-outline'} width={18} />
          </IconButton>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 1 }} />

      {expanded ? (
        <ExpandedView data={filteredData} barColor={barColor} loading={isLoading} onSelect={onSelectTerm} selectedTerms={selectedTerms} paidPresenceLabel={t('dashboard.global.paid_presence_rate')} />
      ) : (
        <CompactView data={filteredData} barColor={barColor} loading={isLoading} onSelect={onSelectTerm} selectedTerms={selectedTerms} paidPresenceLabel={t('dashboard.global.paid_presence_rate')} />
      )}
    </Paper>
  );
}

const COMPACT_LABEL_MAX = 35;
const truncateLabel = (label: string) =>
  label.length > COMPACT_LABEL_MAX ? `${label.slice(0, COMPACT_LABEL_MAX)}…` : label;

function CompactView({ data, barColor = '#C09910', loading, onSelect, selectedTerms, paidPresenceLabel }: { data: Term[]; barColor?: string; loading?: boolean; onSelect?: (s: string) => void; selectedTerms?: string[]; paidPresenceLabel: string }) {
  const [page, setPage] = useState<number>(0);
  const pageSize = 5;
  const totalPages = Math.max(1, Math.ceil((data?.length ?? 0) / pageSize));

  useEffect(() => {
    setPage(0);
  }, [data]);

  const start = page * pageSize;
  const visible = data.slice(start, start + pageSize);
  const showArrows = (data?.length ?? 0) > pageSize;

  return (
    showArrows ? (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
        <IconButton size="small" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} aria-label="Previous keywords page">
          <Iconify icon="eva:arrow-ios-back-outline" width={18} />
        </IconButton>
        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${visible.length}, 1fr)`, alignItems: 'stretch', flex: 1 }}>
          {visible.map((t, idx) => (
            <Box
              key={`${t.label}-${start + idx}`}
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                minWidth: 0,
                overflow: 'hidden',
                ...(idx > 0 && { borderLeft: (theme) => `1px solid ${theme.palette.divider}` }),
              }}
            >
              <Box
                onClick={onSelect ? () => onSelect(t.label) : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                  minWidth: 0,
                  px: 1,
                  py: 0.5,
                  borderRadius: 0,
                  cursor: onSelect ? 'pointer' : 'default',
                  bgcolor: selectedTerms?.includes(t.label) ? 'rgba(145, 158, 171, 0.3)' : 'transparent',
                  '&:hover .search-term-label': onSelect ? {
                    textDecoration: 'underline',
                  } : {},
                }}
              >
              <Tooltip title={t.label.length > COMPACT_LABEL_MAX ? t.label : ""} placement="top" arrow disableInteractive>
                <Typography
                  variant="caption"
                  className="search-term-label"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    fontSize: "13px",
                    fontWeight: "400",
                    transition: 'text-decoration 0.2s',
                  }}
                >
                  {truncateLabel(t.label)}
                </Typography>
              </Tooltip>
              <Tooltip title={paidPresenceLabel} placement="top" arrow disableInteractive>
                <Typography variant="caption" color="text.secondary" sx={{ flex: '0 0 auto', fontSize: "11px", fontWeight: "400" }}>
                  {t.score}%
                </Typography>
              </Tooltip>
              <Tooltip title={paidPresenceLabel} placement="top" arrow disableInteractive>
                <Box sx={{ height: 28, width: 8, borderRadius: 999, bgcolor: selectedTerms?.includes(t.label) ? '#d4d4d4' : 'background.neutral', overflow: 'hidden', position: 'relative' }}>
                  <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${t.score}%`, bgcolor: barColor }} />
                </Box>
              </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
        <IconButton size="small" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} aria-label="Next keywords page">
          <Iconify icon="eva:arrow-ios-forward-outline" width={18} />
        </IconButton>
      </Stack>
    ) : (
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${visible.length}, 1fr)`, alignItems: 'stretch', pb: 0.5 }}>
        {visible.map((t, idx) => (
          <Box
            key={`${t.label}-${idx}`}
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              minWidth: 0,
              overflow: 'hidden',
              ...(idx > 0 && { borderLeft: (theme) => `1px solid ${theme.palette.divider}` }),
            }}
          >
            <Box
              onClick={onSelect ? () => onSelect(t.label) : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                minWidth: 0,
                px: 1,
                py: 0.5,
                borderRadius: 0,
                cursor: onSelect ? 'pointer' : 'default',
                bgcolor: selectedTerms?.includes(t.label) ? 'rgba(145, 158, 171, 0.3)' : 'transparent',
                '&:hover .search-term-label': onSelect ? {
                  textDecoration: 'underline',
                } : {},
              }}
            >
            <Tooltip title={t.label} placement="top" arrow disableInteractive>
              <Typography
                variant="caption"
                className="search-term-label"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  fontSize: "13px",
                  fontWeight: "400",
                  transition: 'text-decoration 0.2s',
                }}
              >
                {truncateLabel(t.label)}
              </Typography>
            </Tooltip>
            <Tooltip title={paidPresenceLabel} placement="top" arrow disableInteractive>
              <Typography variant="caption" color="text.secondary" sx={{ flex: '0 0 auto', fontSize: "11px", fontWeight: "400" }}>
                {t.score}%
              </Typography>
            </Tooltip>
            <Tooltip title={paidPresenceLabel} placement="top" arrow disableInteractive>
              <Box sx={{ height: 28, width: 8, borderRadius: 999, bgcolor: selectedTerms?.includes(t.label) ? '#d4d4d4' : 'background.neutral', overflow: 'hidden', position: 'relative' }}>
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${t.score}%`, bgcolor: barColor }} />
              </Box>
            </Tooltip>
            </Box>
          </Box>
        ))}
      </Box>
    )
  );
}

function ExpandedView({ data, barColor = '#C09910', loading, onSelect, selectedTerms, paidPresenceLabel }: { data: Term[]; barColor?: string; loading?: boolean; onSelect?: (s: string) => void; selectedTerms?: string[]; paidPresenceLabel: string }) {
  const [page, setPage] = useState<number>(0);
  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil((data?.length ?? 0) / pageSize));

  useEffect(() => {
    setPage(0);
  }, [data]);

  const start = page * pageSize;
  const visible = data.slice(start, start + pageSize);
  const showPagination = (data?.length ?? 0) > pageSize;

  return (
    <Box>
      <Stack spacing={1.25}>
        {visible.map((t) => (
          <Stack
            key={t.label}
            direction="row"
            alignItems="center"
            spacing={2}
            onClick={onSelect ? () => onSelect(t.label) : undefined}
            sx={{
              cursor: onSelect ? 'pointer' : 'default',
              px: 1,
              py: selectedTerms?.includes(t.label) ? 0.5 : 0,
              borderRadius: 0.5,
              bgcolor: selectedTerms?.includes(t.label) ? 'rgba(145, 158, 171, 0.3)' : 'transparent',
              '&:hover .search-term-label': onSelect ? {
                textDecoration: 'underline',
              } : {},
            }}
          >
            <TermLabel label={t.label} />

            <Tooltip title={paidPresenceLabel} placement="top" arrow disableInteractive>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40, textAlign: 'right' }}>{t.score}%</Typography>
            </Tooltip>
            <Tooltip title={paidPresenceLabel} placement="top" arrow disableInteractive>
              <Box sx={{ flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={t.score}
                  sx={{ height: 8, borderRadius: 999, bgcolor: selectedTerms?.includes(t.label) ? '#d4d4d4' : 'background.neutral', '& .MuiLinearProgress-bar': { backgroundColor: barColor } }} />
              </Box>
            </Tooltip>
          </Stack>
        ))}
      </Stack>

      {showPagination && (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mt: 2 }}>
          <IconButton size="small" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} aria-label="Previous page">
            <Iconify icon="eva:arrow-ios-back-outline" width={18} />
          </IconButton>
          <Typography variant="caption" color="text.secondary">
            {page + 1} / {totalPages}
          </Typography>
          <IconButton size="small" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} aria-label="Next page">
            <Iconify icon="eva:arrow-ios-forward-outline" width={18} />
          </IconButton>
        </Stack>
      )}
    </Box>
  );
}
function TermLabel({ label }: { label: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [isOverflow, setIsOverflow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setIsOverflow(el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [label]);

  return (
    <Tooltip
      title={isOverflow ? label : ""}
      placement="top"
      arrow
      disableInteractive
      slotProps={{
        popper: {
          modifiers: [{ name: 'offset', options: { offset: [0, -6] } }],
        },
        tooltip: {
          sx: {
            bgcolor: 'common.white',
            color: 'text.primary',
            boxShadow: 1,
            '& .MuiTooltip-arrow': {
              color: 'common.white',
            },
          },
        },
      }}
    >
      <Typography
        ref={ref}
        variant="body2"
        className="search-term-label"
        sx={{
          width: { xs: 160, lg: 220 },
          flexShrink: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          transition: 'text-decoration 0.2s',
        }}
      >
        {label}
      </Typography>
    </Tooltip>
  );
}
